import React, { useState, useEffect } from 'react';
import axios from 'axios';
import usePagination from '../../../utils/usePagination';
import Pagination from '../../../components/common/Pagination';

const API = process.env.REACT_APP_API_URL || 'https://sohaibenterprises.cloud/api';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const EMPTY = { crop_id: '', buyer_id: '', buyer_name: '', quantity_kg: '', price_per_kg: '', sale_date: new Date().toISOString().split('T')[0], payment_status: 'received', notes: '' };
const inputStyle = { width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' };
const labelStyle = { fontSize: '13px', fontWeight: '600', color: '#555', marginBottom: '6px', display: 'block' };
const statusColor = { received: '#27ae60', pending: '#f39c12', partial: '#3498db' };

const HarvestSalesTab = () => {
  const [sales, setSales] = useState([]);
  const [crops, setCrops] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all');

  const fetchAll = () => {
    Promise.all([
      axios.get(`${API}/agriculture/harvest-sales`, { headers: headers() }),
      axios.get(`${API}/agriculture/crops`, { headers: headers() }),
      axios.get(`${API}/agriculture/buyers`, { headers: headers() })
    ]).then(([s, c, b]) => {
      setSales(s.data.data);
      setCrops(c.data.data);
      setBuyers(b.data.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { fetchAll(); }, []);

  const openAdd = () => { setForm(EMPTY); setEditId(null); setShowForm(true); };
  const openEdit = (s) => {
    setForm({ ...s, sale_date: s.sale_date?.split('T')[0] || '', buyer_id: s.buyer_id || '' });
    setEditId(s.id); setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.crop_id || !form.quantity_kg || !form.price_per_kg) return alert('Crop, quantity and price are required');
    setSaving(true);
    try {
      if (editId) await axios.put(`${API}/agriculture/harvest-sales/${editId}`, form, { headers: headers() });
      else await axios.post(`${API}/agriculture/harvest-sales`, form, { headers: headers() });
      setShowForm(false); fetchAll();
    } catch { alert('Failed to save'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this sale?')) return;
    await axios.delete(`${API}/agriculture/harvest-sales/${id}`, { headers: headers() });
    fetchAll();
  };

  const fmt = (n) => `Rs ${Number(n || 0).toLocaleString()}`;
  const totalSales = sales.reduce((s, sale) => s + parseFloat(sale.total_amount || 0), 0);
  const totalQty = sales.reduce((s, sale) => s + parseFloat(sale.quantity_kg || 0), 0);
  const pendingAmount = sales.filter(s => s.payment_status !== 'received').reduce((s, sale) => s + parseFloat(sale.total_amount || 0), 0);
  const filtered = filter === 'all' ? sales : sales.filter(s => s.payment_status === filter);
  const { paginatedData, currentPage, setCurrentPage, totalPages } = usePagination(filtered, 10);

  if (loading) return <div style={{ textAlign: 'center', padding: '60px', color: '#7f8c8d' }}>Loading...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#2c3e50', margin: 0 }}>Harvest Sales</h2>
        <button onClick={openAdd} style={{ padding: '12px 24px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '600', cursor: 'pointer' }}>+ Record Sale</button>
      </div>

      {/* Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        {[
          { label: 'Total Sales', value: sales.length, color: '#3498db', icon: '🛒' },
          { label: 'Total Revenue', value: fmt(totalSales), color: '#27ae60', icon: '💰' },
          { label: 'Total Qty Sold', value: `${totalQty.toLocaleString()} kg`, color: '#9b59b6', icon: '⚖️' },
          { label: 'Pending Payment', value: fmt(pendingAmount), color: '#f39c12', icon: '⏳' },
        ].map(c => (
          <div key={c.label} style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderLeft: `4px solid ${c.color}` }}>
            <div style={{ fontSize: '24px', marginBottom: '6px' }}>{c.icon}</div>
            <div style={{ fontSize: '18px', fontWeight: '700', color: c.color }}>{c.value}</div>
            <div style={{ fontSize: '13px', color: '#7f8c8d' }}>{c.label}</div>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        {['all', 'received', 'pending', 'partial'].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding: '8px 18px', borderRadius: '20px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '13px',
            background: filter === f ? '#27ae60' : '#f0f0f0', color: filter === f ? 'white' : '#555'
          }}>{f.charAt(0).toUpperCase() + f.slice(1)} ({f === 'all' ? sales.length : sales.filter(s => s.payment_status === f).length})</button>
        ))}
      </div>

      <div style={{ background: 'white', borderRadius: '16px', boxShadow: '0 4px 15px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8f9fa' }}>
              {['Crop', 'Buyer', 'Qty (kg)', 'Price/kg', 'Total Amount', 'Sale Date', 'Payment', 'Actions'].map(h => (
                <th key={h} style={{ padding: '14px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#555', borderBottom: '2px solid #eee' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={8} style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>No sales recorded yet.</td></tr>
            ) : paginatedData.map((s, i) => (
              <tr key={s.id} style={{ borderBottom: '1px solid #f0f0f0', background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                <td style={{ padding: '14px 16px', fontWeight: '600', color: '#2c3e50' }}>{s.crop_name || '—'}</td>
                <td style={{ padding: '14px 16px', color: '#555' }}>{s.buyer_name || '—'}</td>
                <td style={{ padding: '14px 16px', color: '#555' }}>{Number(s.quantity_kg).toLocaleString()}</td>
                <td style={{ padding: '14px 16px', color: '#555' }}>{fmt(s.price_per_kg)}</td>
                <td style={{ padding: '14px 16px', fontWeight: '700', color: '#27ae60' }}>{fmt(s.total_amount)}</td>
                <td style={{ padding: '14px 16px', color: '#555' }}>{new Date(s.sale_date).toLocaleDateString('en-PK')}</td>
                <td style={{ padding: '14px 16px' }}>
                  <span style={{ padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', background: `${statusColor[s.payment_status]}20`, color: statusColor[s.payment_status] }}>
                    {s.payment_status}
                  </span>
                </td>
                <td style={{ padding: '14px 16px' }}>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => openEdit(s)} style={{ padding: '6px 14px', background: '#3498db', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>Edit</button>
                    <button onClick={() => handleDelete(s.id)} style={{ padding: '6px 14px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination
  currentPage={currentPage}
  totalPages={totalPages}
  onPageChange={setCurrentPage}
  totalRecords={filtered.length}
  rowsPerPage={10}
/>
      </div>

      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '32px', width: '100%', maxWidth: '560px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#2c3e50', marginBottom: '24px' }}>{editId ? 'Edit Sale' : 'Record Harvest Sale'}</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={labelStyle}>Crop *</label>
                <select style={inputStyle} value={form.crop_id} onChange={e => setForm({ ...form, crop_id: e.target.value })}>
                  <option value="">-- Select Crop --</option>
                  {crops.map(c => <option key={c.id} value={c.id}>{c.crop_name}</option>)}
                </select>
              </div>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={labelStyle}>Buyer *</label>
                <select style={inputStyle} value={form.buyer_id} onChange={e => {
                  const buyer = buyers.find(b => b.id === parseInt(e.target.value));
                  setForm({ ...form, buyer_id: e.target.value, buyer_name: buyer?.buyer_name || '' });
                }}>
                  <option value="">-- Select Buyer --</option>
                  {buyers.map(b => <option key={b.id} value={b.id}>{b.buyer_name}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Quantity (kg) *</label>
                <input style={inputStyle} type="number" value={form.quantity_kg} onChange={e => setForm({ ...form, quantity_kg: e.target.value })} placeholder="0" />
              </div>
              <div>
                <label style={labelStyle}>Price per kg (Rs) *</label>
                <input style={inputStyle} type="number" value={form.price_per_kg} onChange={e => setForm({ ...form, price_per_kg: e.target.value })} placeholder="0" />
              </div>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={labelStyle}>Total Amount</label>
                <input style={{ ...inputStyle, background: '#f8f9fa', color: '#27ae60', fontWeight: '700' }}
                  value={`Rs ${(parseFloat(form.quantity_kg || 0) * parseFloat(form.price_per_kg || 0)).toLocaleString()}`} readOnly />
              </div>
              <div>
                <label style={labelStyle}>Sale Date</label>
                <input style={inputStyle} type="date" value={form.sale_date} onChange={e => setForm({ ...form, sale_date: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Payment Status</label>
                <select style={inputStyle} value={form.payment_status} onChange={e => setForm({ ...form, payment_status: e.target.value })}>
                  <option value="received">Received</option>
                  <option value="pending">Pending</option>
                  <option value="partial">Partial</option>
                </select>
              </div>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={labelStyle}>Notes</label>
                <textarea style={{ ...inputStyle, height: '70px', resize: 'vertical' }} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Any notes..." />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowForm(false)} style={{ padding: '12px 24px', background: '#f0f0f0', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={{ padding: '12px 24px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>
                {saving ? 'Saving...' : editId ? 'Update Sale' : 'Record Sale'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HarvestSalesTab;